import { withStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { FaAdjust, FaCheckCircle, FaExclamationCircle, FaRegCircle, FaTrashAlt } from "react-icons/fa";
import { DocumentUpload } from "../../../Shared/DocumentUpload";
import { isOverflowingX } from "../../../../shared/utilities/isOverflowing";

// Import CSS
import "./AttachmentsPage.css";

const sx = {
  tooltip: {
    color: "black",
    backgroundColor: "white",
    border: "1px solid #E6E8ED",
    fontSize: 14,
  },
  arrow: {
    color: "white",
    "&:before": {
      border: "1px solid #E6E8ED",
    },
  }
};

const StyledTooltip = withStyles(sx)(Tooltip);

// Show tooltip only if text is truncated
const CustomTooltip = (props) => {
  return <StyledTooltip
    {...props}
    disableFocusListener
    disableTouchListener
    disableHoverListener={props.target && !isOverflowingX(props.target)}
  />;
};

const AttachmentsPage = ({ incrementCounter, decrementCounter, requestCallback, phaseId, projectId, formState, requirements, optional }) => {  
  const [showValidation, setShowValidation] = useState(false);
  const [uploads, setUploads] = useState([]);

  const [requiredDropdown, setRequiredDropdown] = useState([]);
  const [optionalDropdown, setOptionalDropdown] = useState([]);
  const [unfilledRequirements, setUnfilledRequirements] = useState();
  const [isValid, setIsValid] = useState(false);

  // Restore uploads from formState when returning to this page
  useEffect(() => {
    if (formState && formState.selectedFiles) {
      setUploads(formState.selectedFiles);
    }
  }, [formState]);

  useEffect(() => {
    if (optional && requirements && uploads) {
      const allDocs = requirements.flatMap(r => r.docs);
      if (!allDocs.length) {
        console.log('No required documents found', requirements);
        incrementCounter();
        return;
      }

      const addedDocTypes = uploads.map(fileObj => fileObj?.docType?.documentRankingId);
      const addedSet = new Set(addedDocTypes);

      // Count how many distinct docs from each group have been uploaded
      const uploadCounts = requirements.map(({ count, docs }) => {
        const matchCount = docs.filter(d => addedSet.has(d.documentRankingId)).length;
        return { count, matchCount, docs };
      });

      // Remaining: docs in groups where matchCount < count (only unfulfilled docs)
      const remainingNested = uploadCounts.map(({ count, matchCount, docs }) =>
        matchCount >= count ? [] : docs.filter(d => !addedSet.has(d.documentRankingId))
      );
      // Fulfilled: docs in groups where matchCount >= count
      const fulfilledNested = uploadCounts.map(({ count, matchCount, docs }) =>
        matchCount >= count ? docs : docs.filter(d => addedSet.has(d.documentRankingId))
      );

      const remainingFlat = new Set(remainingNested.flat());
      const fulfilled = new Set(fulfilledNested.flat());
      const fulfilledOrOptional = new Set([ ...fulfilled, ...optional.flat() ]);

      setRequiredDropdown([ ...remainingFlat ]);
      setOptionalDropdown([ ...fulfilledOrOptional ]);
      setUnfilledRequirements(uploadCounts);
    }    
  }, [optional, requirements, uploads]);

  useEffect(() => {
    if (unfilledRequirements && requiredDropdown) {
      setIsValid(!requiredDropdown.length);
    }
  }, [unfilledRequirements, requiredDropdown])

  function handleAddFile(fileObj) {
    const files = [ ...uploads, fileObj ];    
    setUploads(files);
  }

  function handleRemoveFile(index) {
    const files = [ ...uploads ];    
    files.splice(index, 1);
    setUploads(files);
    setShowValidation(true);
  }

  function handleBackButton(e) {
    e.preventDefault();    
    decrementCounter(formState);
  }

  async function handleSubmit(e) {
    e.preventDefault();    

    setShowValidation(true);
    
    try {
      if (isValid) {
        // Instead of uploading files now, just save the file list to form state
        // Files will be uploaded when the user clicks "Submit Request" in ReviewAndSubmitPage
        const formStateWithFiles = {
          ...formState,
          selectedFiles: uploads,
          requiredDocuments: requirements.flatMap(r => r.docs)
        };
        
        incrementCounter(formStateWithFiles);
      }
    } catch (error) {
      console.error('handleSubmit error', error);
    }
  }

  function describeRequirement({ count, docs }) {
    const labels = docs.map(d => d.name);

    if (docs.length === 1) return labels[0];
    if (count >= docs.length) return labels.join(" and ");

    const joined = labels.length === 2
      ? `${labels[0]} or ${labels[1]}`
      : `${labels.slice(0, -1).join(", ")}, or ${labels[labels.length - 1]}`;

    return count === 1
      ? `One of: ${joined}`
      : `${count} of: ${joined}`;
  }

  function getProgress({ count, matchCount }) {
    if (matchCount >= count) return null; // fulfilled
    return `(${matchCount} of ${count} uploaded)`;
  }

  function getBullet(index) {
    const entry = unfilledRequirements[index];
    if (!entry) return <FaRegCircle className="req-bullet default" />;

    const { count, matchCount } = entry;

    if (matchCount >= count) {
      return <FaCheckCircle className="req-bullet completed" />;
    }
    if (matchCount > 0) {
      return <FaAdjust className="req-bullet partial" />;
    }
    // empty
    return showValidation
      ? <FaExclamationCircle className="req-bullet error" />
      : <FaRegCircle className="req-bullet default" />;
  }

  const fileNameRefs = useRef([]);
  const fileNoteRefs = useRef([]);
  const fileDocTypeRefs = useRef([]);

  return (
    <form className={"upload-docs-form " + (showValidation ? "show-validation" : "")}>
      <section className="title-section">
        <h1>{formState?.requestType || "Status Review"} Request</h1>
        <h6>Section 3 of 5 - Select Documents</h6>
      </section>
    
      {unfilledRequirements ? (
        <section className="main-section">
          <p>Please select the following documents to upload with your request:</p>
          <ul className="req-list">
            {requirements.map((req, index) => 
              <li key={index}>
                {getBullet(index)}
                <div className="req-desc">
                  {describeRequirement(req)}
                  {unfilledRequirements?.[index] && getProgress(unfilledRequirements[index]) &&
                    <span className="req-progress"> {getProgress(unfilledRequirements[index])}</span>
                  }
                </div>
              </li>
            )}
          </ul>
          {uploads.length ? ( 
            <div className="uploaded-files-list">
              <div className="uploaded-files-row header-row">
                <div className="file-doctype">Document Type</div>
                <div className="file-status-bar">Selected File</div>             
                <div className="file-note">Note</div>
              </div>
              {uploads.map((fileObj, index) => (
                <div key={index} className="uploaded-files-row">
                  <CustomTooltip 
                    target={fileDocTypeRefs.current[index]} 
                    title={fileObj.docType.name} 
                    placement="top"
                  >
                    <div ref={ref => fileDocTypeRefs.current[index] = ref} className="file-doctype">
                      {fileObj.docType.name}
                    </div>
                  </CustomTooltip>

                  <div className="file-status-bar">
                    <CustomTooltip target={fileNameRefs.current[index]} title={fileObj.file.name} placement="top">
                      <div ref={ref => fileNameRefs.current[index] = ref} className="file-name">
                        {fileObj.file.name}
                      </div>
                    </CustomTooltip>
                    <div className="file-remove" onClick={() => handleRemoveFile(index)}>
                      <FaTrashAlt className="delete-button" />
                    </div>
                  </div>        

                  <CustomTooltip 
                    target={fileNoteRefs.current[index]} 
                    title={fileObj.note} 
                    placement="top"
                  >
                    <div ref={ref => fileNoteRefs.current[index] = ref} className="file-note">
                      {fileObj.note ? fileObj.note : (
                        <span className="empty-field">--</span>
                      )}
                    </div>
                  </CustomTooltip>
                </div>
              ))}
            </div>
          ) : (<></>)}
          <DocumentUpload
            requiredList={requiredDropdown} 
            optionalList={optionalDropdown} 
            onAddFile={fileObj => handleAddFile(fileObj)}
            />
        </section>
      ) : (<></>)}

      <div className="stepper-controls" style={{marginTop: "40px"}}>
        <div className="control-cell">
          <Button
            type="submit"
            className="back-button"
            onClick={handleBackButton}
            form="addProjectForm"
          >
            Back
          </Button>
        </div>
        <div className="control-cell">
          <Button
            type="submit"
            className={"btn-submit" + 
              (!isValid ? " btn-invalid" : "")
            }
            onSubmit={handleSubmit}
            onClick={handleSubmit}
            form="addProjectForm"
          >
            Next
          </Button>
        </div>
      </div>
    </form>
  );
};

export default AttachmentsPage;
